/**
 * Pure helpers: numbers, randomness and easing.
 *
 * This module imports only Clock and Constants, so any module can use
 * it without pulling the rest of the game in behind it. Anything that touches
 * storage, the network, rendering or the UI has its own module: Settings,
 * Http, Reel, Debug, Device, NameCleanup and ErrorReport.
 */
import { clock } from './Clock';
import { TWO_PI } from './Constants';

/** Scaling factor for the rank/earnings curve. */
const RANK_SCALING_FACTOR = 0.0111;

/** Euclidean length of a 2D vector (faster than Math.hypot). */
export function length(x: number, y: number) {
  return Math.sqrt(x * x + y * y);
}

/** Sine-wave oscillator centered on 1.0. Period is a divisor (actual period = period * 2pi ms). */
export function oscillator(amplitude: number, period: number, timeOffset: number = 0) {
  return 1 + Math.sin((clock.time + timeOffset) / period) * amplitude;
}

/** Lerps current toward target, snapping when within 0.01. */
export function converge(current: number, target: number, factor: number) {
  const SNAP_THRESHOLD = 0.01;
  return Math.abs(current - target) < SNAP_THRESHOLD ? target : current + factor * (target - current);
}

/** Random float in [min, max). */
export function rand(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

/** Random angle in [0, 2pi). */
export function randCircle() {
  return rand(0, TWO_PI);
}

/** Random integer in [min, max] (inclusive). */
export function randInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/** A random element of a list that is never empty. */
export function randItem<T>(list: readonly T[]): T {
  return list[Math.floor(Math.random() * list.length)] as T;
}

/** Generates a cryptographically random hex string of the given length. */
export function randomID(length: number) {
  // Calculate bytes needed: 2 hex chars per byte, round up for odd lengths
  const numBytes = Math.ceil(length / 2);
  const randomBytes = new Uint8Array(numBytes);
  window.crypto.getRandomValues(randomBytes);

  const hexString = bytesToHex(randomBytes);
  return hexString.slice(0, length);
}

/** Converts a byte array to a lowercase hex string. */
function bytesToHex(bytes: Uint8Array) {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Clamps value to [min, max]. */
export function clamp(value: number, min: number, max: number) {
  if (value <= min) {
    return min;
  }
  if (value >= max) {
    return max;
  }
  return value;
}

/** Linear interpolation: returns start when t=0, end when t=1. */
function lerp(start: number, end: number, t: number) {
  return t * (end - start) + start;
}

/** Lerps between two 0xRRGGBB colors per-channel. */
export function colorLerp(startColor: number, endColor: number, t: number): number {
  // Clamp t to avoid edge cases
  if (t <= 0) {
    t = 0.001;
  }
  if (t >= 1) {
    t = 0.999;
  }

  // Extract RGB channels from start color
  const startRed = startColor >> 16;
  const startGreen = (startColor >> 8) & 0xff;
  const startBlue = startColor & 0xff;

  // Extract RGB channels from end color
  const endRed = endColor >> 16;
  const endGreen = (endColor >> 8) & 0xff;
  const endBlue = endColor & 0xff;

  // Lerp each channel
  const lerpedRed = startRed + t * (endRed - startRed);
  const lerpedGreen = startGreen + t * (endGreen - startGreen);
  const lerpedBlue = startBlue + t * (endBlue - startBlue);

  // Pack channels back into a 24-bit RGB integer (PixiJS 8 requires plain RGB, no alpha)
  const packedColor = (lerpedRed << 16) | (lerpedGreen << 8) | lerpedBlue;

  return packedColor | 0; // Convert to signed 32-bit integer
}

/** Euclidean distance between two points. */
export function distance(x1: number, y1: number, x2: number, y2: number) {
  const deltaX = x1 - x2;
  const deltaY = y1 - y2;
  return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
}

/** The squared distance between two points, for comparisons that need no root. */
export function distanceSq(x1: number, y1: number, x2: number, y2: number) {
  const deltaX = x1 - x2;
  const deltaY = y1 - y2;
  return deltaX * deltaX + deltaY * deltaY;
}

/** AABB proximity check with a single square threshold. */
export function distFastCheckFloat(x1: number, y1: number, x2: number, y2: number, maxDistance: number) {
  return Math.abs(x1 - x2) <= maxDistance && Math.abs(y1 - y2) <= maxDistance;
}

/** Named easing curve lookup tables (sampled at regular intervals). */
const easingByName = {
  shockwave: [
    0.1, 0.1, 0.11, 0.12, 0.12, 0.13, 0.14, 0.14, 0.15, 0.16, 0.17, 0.18, 0.2, 0.21, 0.22, 0.24, 0.26, 0.29, 0.31, 0.35,
    0.38, 0.42, 0.47, 0.52, 0.58, 0.64, 0.71, 0.78, 0.84, 0.9, 0.95, 0.98, 1, 1, 1, 0.98, 0.97, 0.94, 0.9, 0.85, 0.78,
    0.7, 0.62, 0.52, 0.43, 0.34, 0.26, 0.18, 0.11, 0.05, 0,
  ],
  explosionSmoke: [
    0, 0, 0.02, 0.06, 0.13, 0.26, 0.45, 0.71, 0.91, 0.99, 0.99, 0.97, 0.94, 0.92, 0.89, 0.86, 0.83, 0.8, 0.77, 0.74,
    0.71, 0.68, 0.65, 0.63, 0.6, 0.57, 0.54, 0.51, 0.48, 0.45, 0.42, 0.4, 0.37, 0.34, 0.31, 0.29, 0.26, 0.24, 0.21,
    0.19, 0.16, 0.14, 0.12, 0.1, 0.08, 0.06, 0.04, 0.02, 0.01, 0, 0,
  ],
} as const satisfies Record<string, readonly number[]>;

/** A curve name accepted by easing.custom. */
export type EasingCurveName = keyof typeof easingByName;

/** Easing functions for animations and transitions. */
export const easing = {
  /** Elastic out with oscillation decay. */
  outElastic(t: number, amplitude: number = 0.7) {
    const OSCILLATION_MULTIPLIER = 2;
    const DECAY_RATE = -10;

    // Use default amplitude if falsy (handles 0 or undefined)
    const effectiveAmplitude = amplitude || 0.7;
    const period = 1 - effectiveAmplitude;
    const doubled = OSCILLATION_MULTIPLIER * t;

    // Handle edge cases (start and end)
    if (t === 0 || t === 1) {
      return t;
    }

    // Calculate phase shift for the sine wave
    const phaseShift = (period / TWO_PI) * Math.asin(1);

    // Apply elastic easing formula
    return Math.pow(2, DECAY_RATE * doubled) * Math.sin(((doubled - phaseShift) * TWO_PI) / period) + 1;
  },

  /** Samples and interpolates from a named easing curve. */
  custom(t: number, curveName: EasingCurveName) {
    const curve = easingByName[curveName];
    const lastIndex = curve.length - 1;
    const scaled = t * lastIndex;
    const index = Math.floor(scaled);
    const currentValue = curve[index];

    // Off the end of the table, so t was outside [0, 1]. Only a particle on
    // the frame it dies gets here, and its alpha is never drawn.
    if (currentValue === undefined) {
      return t;
    }

    // At the last sample, return it directly
    if (index === lastIndex) {
      return currentValue;
    }

    // Interpolate between current and next sample
    return lerp(currentValue, curve[index + 1]!, scaled % 1);
  },
};

/** Minimum earnings needed for a given rank. */
export function rankToEarnings(rank: number) {
  return Math.pow((rank - 1) / RANK_SCALING_FACTOR, 2);
}
